// Anita Week 4 / Day 5 — Week Project: Map + Water-Stays-in-the-Jar Experiment.
// Combines route planning on a 10×10 grid, perimeter, fractions, division as
// meters-per-minute, mean of 3 trials, and a hands-on surface-tension activity.
// SAFETY: only with a parent, only over the sink or basin, no heavy jars.

const day5 = {
  id: 'day-5',
  studentId: 'anita',
  weekId: 'week-4',
  dayId: 'day-5',
  status: 'active',
  title: 'Проект недели: карта + опыт «Вода не проливается» / Week Project: Map + Water Stays in the Jar',
  shortTitle: 'Проект недели / Week Project',
  theme:
    'Итог недели: карта безопасного маршрута и физический опыт про поверхностное натяжение. / Week wrap-up: safe-route map + a hands-on surface-tension experiment.',
  estimatedTimeLabel: '80–90 минут / 80–90 minutes',
  lessonTiming: { minMinutes: 80, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'map', 'physics', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни поздравляет Аниту с проектом недели. Сегодня Анита рисует свою карту, считает участки маршрута и делает домашний опыт: «Вода не проливается через ткань». / Nimny celebrates the week project. Today Anita draws her map and does a surface-tension experiment.',
  goals: [
    'Собрать карту маршрута из 3 участков на сетке. / Assemble a 3-section route on a grid.',
    'Найти периметр прямоугольного листа карты. / Find the perimeter of the map sheet.',
    'Работать с 1/2 и 1/4 от 40. / Use 1/2 and 1/4 of 40.',
    'Считать время маршрута через деление (метры ÷ метры/мин). / Compute travel time via division.',
    'Найти среднее по 3 попыткам опыта. / Find the mean of 3 experiment trials.',
    'Провести опыт «Вода не проливается» с родителем. / Do the water-stays experiment with a parent.'
  ],
  vocabulary: [
    { term: 'вода / water', definition: 'жидкость. / a liquid.' },
    { term: 'ткань / cloth', definition: 'мягкий материал. / soft material.' },
    { term: 'банка / jar', definition: 'прозрачный стакан или банка. / clear glass jar.' },
    { term: 'опыт / experiment', definition: 'проверка того, что происходит. / a test of what happens.' },
    { term: 'вывод / conclusion', definition: 'что я понял после опыта. / what I understood.' },
    { term: 'держится / holds', definition: 'не проливается. / does not spill.' }
  ],
  onlineTasks: [
    {
      id: 'reading-review-words',
      type: 'text',
      title: 'Задание 1 — Слова недели / Week Review Words',
      prompt: 'Прочитай по-русски:\nкарта\nсетка\nмасштаб\nмаршрут\nбезопасный\nвода\nткань\nопыт\nвывод\nдержится',
      ruPrimaryText: 'Прочитай 10 слов недели.',
      enHelpText: 'Read 10 review words: map, grid, scale, route, safe, water, cloth, experiment, conclusion, holds.',
      answer: 'Анита пробует читать 10 слов.'
    },
    {
      id: 'oral-defense-reading',
      type: 'text',
      title: 'Задание 2 — Защита проекта / Project Defense',
      prompt:
        'Прочитай по-русски:\nЯ нарисовала свою карту.\nМой маршрут проходит по клеткам.\nЯ проверила масштаб.\nЯ сделала опыт с водой и тканью.\nВода держалась благодаря поверхностному натяжению.',
      ruPrimaryText: 'Прочитай 5 фраз защиты проекта.',
      enHelpText: 'I drew my map. My route runs across the cells. I checked the scale. I did the water experiment. The water held because of surface tension.',
      answer: 'Анита пробует читать 5 фраз защиты.'
    },
    {
      id: 'math-map-route',
      type: 'text',
      title: 'Задание 3 — Карта маршрута / Route Map',
      prompt:
        'Анита рисует карту маршрута.\nМаршрут состоит из 5 клеток до дерева, 4 клеток до моста и 3 клеток до домика Нимни.\nОдна клетка = 6 метров.\n\nОтветь:\n1. Сколько клеток всего?\n2. Сколько метров весь маршрут?',
      ruPrimaryText: 'Сумма клеток трёх участков + умножение на масштаб 6 м.',
      enHelpText: 'Add the cells first, then multiply by 6.',
      answer: '1) 5 + 4 + 3 = 12 клеток. 2) 12 × 6 = 72 метра.'
    },
    {
      id: 'math-map-perimeter',
      type: 'text',
      title: 'Задание 4 — Периметр карты / Map Perimeter',
      prompt:
        'Лист карты имеет форму прямоугольника.\nДлина 16 см, ширина 11 см.\n\nОтветь:\n1. Периметр через сумму 4 сторон?\n2. Проверь через 2 × (длина + ширина).',
      ruPrimaryText: 'Периметр прямоугольника двумя способами.',
      enHelpText: 'Add all four sides; check 2 × (16 + 11).',
      answer: '1) 16 + 11 + 16 + 11 = 54 см. 2) 2 × (16 + 11) = 2 × 27 = 54 см.'
    },
    {
      id: 'math-map-points',
      type: 'text',
      title: 'Задание 5 — Точки на карте / Map Points',
      prompt:
        'На карте 40 точек.\n1/2 точек — ориентиры.\n1/4 точек — безопасные места.\nОстальные точки — места для проверки.\n\nОтветь:\n1. Сколько ориентиров?\n2. Сколько безопасных мест?\n3. Сколько мест нужно проверить?',
      ruPrimaryText: '1/2 и 1/4 от 40, потом остаток.',
      enHelpText: 'Half of 40, then one fourth of 40, then subtract from 40.',
      answer: '1) 1/2 от 40 = 20. 2) 1/4 от 40 = 10. 3) 40 − 20 − 10 = 10 мест для проверки.'
    },
    {
      id: 'math-travel-time',
      type: 'text',
      title: 'Задание 6 — Время маршрута / Travel Time',
      prompt:
        'Анита идёт 8 метров за 1 минуту.\nВесь маршрут 72 метра.\n\nОтветь:\n1. Сколько минут займёт маршрут?',
      ruPrimaryText: 'Деление: расстояние ÷ метры в минуту.',
      enHelpText: 'Divide 72 by 8.',
      answer: '72 ÷ 8 = 9 минут.'
    },
    {
      id: 'math-experiment-trials',
      type: 'text',
      title: 'Задание 7 — Таблица опыта / Experiment Table',
      prompt:
        'Анита сделала 3 попытки опыта.\nПопытка 1: вода держалась 8 секунд.\nПопытка 2: 12 секунд.\nПопытка 3: 10 секунд.\n\nОтветь:\n1. Сколько секунд всего?\n2. Сколько секунд в среднем (на одну попытку)?',
      ruPrimaryText: 'Сумма 3 попыток и среднее через деление на 3.',
      enHelpText: 'Add the seconds, then divide by 3.',
      answer: '1) 8 + 12 + 10 = 30 секунд. 2) 30 ÷ 3 = 10 секунд в среднем.'
    },
    {
      id: 'physics-water-cloth',
      type: 'text',
      title: 'Задание 8 — Опыт «Вода не проливается через ткань» / Water Stays in the Jar',
      prompt:
        'Материалы:\nбанка или прозрачный стакан;\nтонкая ткань (марля);\nрезинка;\nвода;\nзубочистка;\nтаз или раковина.\n\nШаги:\n1. Натяни ткань на горлышко банки.\n2. Закрепи резинкой.\n3. Налей воду через ткань.\n4. Прикрой горлышко рукой.\n5. Аккуратно переверни банку над раковиной или тазом.\n6. Медленно убери руку.\n7. Наблюдай, почему вода не выливается сразу.\n8. Аккуратно попробуй просунуть зубочистку через ткань.\n\nЗапиши наблюдения:\nЧто я сделала:\nЧто я увидела:\nЧто изменилось:\nПочему, как я думаю:\nМой вывод:\n\nБезопасность: только с родителем, только над раковиной/тазом, не тяжёлая банка, не трясти банку.',
      ruPrimaryText: 'Проведи опыт с родителем и заполни поля наблюдений.',
      enHelpText: 'Do the experiment with a parent over the sink. Record what you did, saw, changed, thought and concluded.',
      hint: 'Опыт делать только с родителем и только над раковиной. / Only with a parent, only over the sink.',
      answer: 'Open-ended. Ожидаемо: вода держится из-за поверхностного натяжения; с большими дырочками вода потечёт быстрее.'
    },
    {
      id: 'logic-experiment-why',
      type: 'text',
      title: 'Задание 9 — Логика опыта / Experiment Logic',
      prompt:
        'Ответь 1–3 предложениями:\n1. Почему вода не сразу вылилась через ткань?\n2. Что изменится, если ткань будет с большими дырочками?\n3. Что изменится, если банку трясти?',
      ruPrimaryText: 'Объясни поверхностное натяжение своими словами.',
      enHelpText: 'Surface tension holds water in small cloth holes. Bigger holes or shaking break the film.',
      answer: 'Ожидаемо: «Плёнка воды на маленьких дырочках держит воду. Большие дырочки или тряска разрушают плёнку.»'
    },
    {
      id: 'spelling-day5',
      type: 'text',
      title: 'Задание 10 — Правописание недели / Week Spelling',
      prompt:
        'Напиши:\nвода\nткань\nбанка\nдержится\nопыт\nвывод\n\nВставь буквы:\nв_да\nтк_нь\nба_ка\nде_жится\nо_ыт\nвы_од',
      ruPrimaryText: 'Напиши 6 слов опыта и вставь буквы.',
      enHelpText: 'Write 6 experiment words and fill in the letters.',
      answer: 'вода, ткань, банка, держится, опыт, вывод.'
    },
    {
      id: 'speech-day5',
      type: 'text',
      title: 'Задание 11 — Защита проекта родителю / Defend to Parent',
      prompt:
        'Скажи родителю по-русски:\nЯ нарисовала карту и маршрут.\nЯ сделала опыт с водой и тканью.\nВода держалась благодаря поверхностному натяжению.\nМой вывод: маленькие отверстия ткани могут держать воду.',
      ruPrimaryText: 'Расскажи родителю про карту, опыт и вывод по-русски.',
      enHelpText: 'Tell the parent: I drew a map, I did the experiment, water held because of surface tension.',
      answer: 'Accept if Anita defends the project in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Начерти сетку 10×10 и подпиши масштаб 6 м.',
    '2. Расставь дом, дерево, мост, домик Нимни.',
    '3. Реши задачи 3–7.',
    '4. Проведи опыт с родителем и запиши 3 попытки.',
    '5. Заполни поля наблюдений.',
    '6. Расскажи родителю итог проекта.'
  ],
  printWorksheet: {
    title: 'Проект недели: карта + опыт «Вода не проливается» / Week Project + Water Experiment',
    subtitle: 'Неделя 4 · День 5 / Week 4 · Day 5',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: сегодня ты собираешь карту недели и делаешь опыт «Вода не проливается через ткань». / Today you assemble your week map and run the water-stays experiment.' },
          { type: 'text', text: 'Задание 1 — Слова недели. Прочитай и обведи:' },
          { type: 'list', items: [
            'карта / map',
            'сетка / grid',
            'масштаб / scale',
            'маршрут / route',
            'безопасный / safe',
            'вода / water',
            'ткань / cloth',
            'опыт / experiment',
            'вывод / conclusion',
            'держится / holds'
          ] },
          { type: 'text', text: 'Задание 2 — Защита проекта. Прочитай:' },
          { type: 'list', items: [
            '□ Я нарисовала свою карту.',
            '□ Мой маршрут проходит по клеткам.',
            '□ Я проверила масштаб.',
            '□ Я сделала опыт с водой и тканью.',
            '□ Вода держалась благодаря поверхностному натяжению.'
          ] },
          { type: 'text', text: 'Задание 3 — Карта маршрута. Готовая сетка 10 × 10. 1 клетка = 6 м. Расставь: Дом (S), Дерево (T), Мост (B), Домик Нимни (H). Нарисуй путь по клеткам.' },
          { type: 'list', items: [
            'y',
            '10 | □ □ □ □ □ □ □ □ □ □',
            ' 9 | □ □ □ □ □ □ □ □ □ □',
            ' 8 | □ □ □ □ □ □ □ □ □ □',
            ' 7 | □ □ □ □ □ □ □ □ □ □',
            ' 6 | □ □ □ □ □ □ □ □ □ □',
            ' 5 | □ □ □ □ □ □ □ □ □ □',
            ' 4 | □ □ □ □ □ □ □ □ □ □',
            ' 3 | □ □ □ □ □ □ □ □ □ □',
            ' 2 | □ □ □ □ □ □ □ □ □ □',
            ' 1 | S □ □ □ □ □ □ □ □ □',
            '     1 2 3 4 5 6 7 8 9 10  x',
            'Дом → Дерево: 5 клеток',
            'Дерево → Мост: 4 клетки',
            'Мост → Домик Нимни: 3 клетки',
            'Клеток всего: ___',
            'Метров всего (× 6): ___'
          ] }
        ]
      },
      {
        title: 'Периметр, точки, время / Perimeter, Points, Time',
        blocks: [
          { type: 'text', text: 'Задание 4 — Периметр карты. Прямоугольник 16 × 11 см.' },
          { type: 'list', items: [
            'верх = 16 см',
            'низ  = 16 см',
            'лево = 11 см',
            'право = 11 см',
            'Периметр = ___ + ___ + ___ + ___ = ___ см',
            'Проверь: 2 × (16 + 11) = 2 × ___ = ___ см'
          ] },
          { type: 'text', text: 'Задание 5 — Точки на карте. Готовая таблица:' },
          { type: 'list', items: [
            'Всего точек               | 40',
            'Ориентиры (1/2)           | 1/2 от 40 = ___',
            'Безопасные места (1/4)    | 1/4 от 40 = ___',
            'Места для проверки        | 40 − ___ − ___ = ___'
          ] },
          { type: 'text', text: 'Задание 6 — Время маршрута. Скорость: 8 метров за 1 минуту. Весь маршрут — 72 м.' },
          { type: 'list', items: [
            'Время = 72 ÷ 8 = ___ минут'
          ] },
          { type: 'text', text: 'Задание 7 — Таблица опыта. Заполни 3 попытки:' },
          { type: 'list', items: [
            'Попытка | Секунды',
            '   1    |   8',
            '   2    |   12',
            '   3    |   10',
            'Всего: ___',
            'В среднем (÷ 3): ___'
          ] },
          { type: 'text', text: 'Задание 8 — Опыт. Материалы: банка/стакан, тонкая ткань (марля), резинка, вода, зубочистка, таз/раковина.' },
          { type: 'list', items: [
            '1. Натяни ткань на горлышко.',
            '2. Закрепи резинкой.',
            '3. Налей воду через ткань.',
            '4. Прикрой горлышко рукой.',
            '5. Аккуратно переверни над раковиной.',
            '6. Медленно убери руку.',
            '7. Наблюдай, почему вода не выливается.',
            '8. Аккуратно попробуй просунуть зубочистку.'
          ] },
          { type: 'text', text: 'Что я сделала: __________________________________________________________________' },
          { type: 'text', text: 'Что я увидела: __________________________________________________________________' },
          { type: 'text', text: 'Что изменилось: __________________________________________________________________' },
          { type: 'text', text: 'Почему, как я думаю: _____________________________________________________________' },
          { type: 'text', text: 'Мой вывод: ______________________________________________________________________' },
          { type: 'text', text: 'Безопасность: только с родителем; только над раковиной или тазом; не тяжёлая банка; не трясти банку.' }
        ]
      },
      {
        title: 'Логика, русский и защита / Logic, Russian, Defense',
        blocks: [
          { type: 'text', text: 'Задание 9 — Логика опыта. Ответь 1–3 предложения на каждый вопрос:' },
          { type: 'list', items: [
            'Почему вода не сразу вылилась через ткань? _______________________________',
            'Что изменится, если ткань с большими дырочками? __________________________',
            'Что изменится, если банку трясти? ________________________________________'
          ] },
          { type: 'text', text: 'Задание 10 — Правописание недели. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'вода   ткань   банка   держится   опыт   вывод',
            'в_да    тк_нь    ба_ка    де_жится    о_ыт    вы_од'
          ] },
          { type: 'text', text: 'Задание 11 — Защита родителю. Скажи:' },
          { type: 'list', items: [
            '□ Я нарисовала карту и маршрут.',
            '□ Я сделала опыт с водой и тканью.',
            '□ Вода держалась благодаря поверхностному натяжению.',
            '□ Мой вывод: маленькие отверстия ткани могут держать воду.'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала 10 слов  □ прочитала 5 фраз защиты  □ карта 10×10 с S/T/B/H  □ периметр 16×11  □ 1/2 и 1/4 от 40  □ 72 ÷ 8 = 9  □ 3 попытки опыта  □ опыт проведён над раковиной  □ 3 логических ответа  □ 6 слов недели  □ защитила проект.' },
          { type: 'text', text: 'Notebook Mode fallback: если нет принтера, начерти сетку 10×10, таблицы попыток и полей наблюдений в тетради и повтори все задания.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита собирает недельный проект: карта маршрута, периметр карты, точки, время маршрута и физический опыт про поверхностное натяжение.',
    mathGoal: 'U.S. Grade 3 обзор: сумма клеток × масштаб, периметр 16×11, 1/2 и 1/4 от 40, 72 ÷ 8, сумма и среднее 3 попыток.',
    russianGoal: 'Прочитать 10 слов недели и 5 фраз защиты; написать 6 слов и вставить буквы.',
    readingWritingGoal: '3 логических ответа + защита проекта.',
    practicalGoal: 'Провести опыт «Вода не проливается» с родителем и заполнить поля наблюдений.',
    rubric: [
      'Русское чтение (10 слов + защита) — 20 баллов / Russian reading and defense — 20 points',
      'Математика (Math 3–7 с шагами) — 30 баллов / Math correctness with steps — 30 points',
      'Периметр 16 × 11 — 5 баллов / Perimeter — 5 points',
      'Дроби 1/2 и 1/4 от 40 — 5 баллов / Fractions 1/2 and 1/4 of 40 — 5 points',
      'Опыт: 3 попытки + поля наблюдений — 20 баллов / Experiment 3 trials + fields — 20 points',
      'Безопасность опыта (родитель + раковина) — 5 баллов / Safety — 5 points',
      'Логика опыта (3 ответа) — 10 баллов / Experiment logic — 10 points',
      'Старательность и фокус — 5 баллов / Effort and focus — 5 points'
    ],
    parentTheoryCard: {
      title: 'Как объяснить поверхностное натяжение',
      purpose: 'Помочь родителю сделать опыт безопасно и объяснить физику простыми словами.',
      keyMessage: 'В маленьких дырочках ткани появляется тонкая «водная плёнка», и вода не выливается сразу.',
      parentScript: [
        '«Смотри: ткань перекрывает горлышко. Мы наливаем воду и переворачиваем банку.»',
        '«Маленькие дырочки ткани не пропускают воду сразу — сила натяжения держит плёнку.»',
        '«Если ткань с большими дырочками — плёнка сразу порвётся, вода польётся.»',
        '«Если банку трясти — плёнка тоже порвётся.»'
      ],
      practiceSteps: [
        'Работайте только над раковиной или тазом.',
        'Используйте лёгкую банку или прозрачный пластиковый стакан.',
        'Ткань должна быть натянута и закреплена резинкой.',
        'Медленно переверните банку и медленно уберите руку.',
        'Попробуйте 3 попытки и запишите время в таблицу.'
      ],
      doNotSay: [
        'Не используй тяжёлую стеклянную банку.',
        'Не тряси банку — это опасно и портит опыт.',
        'Не оставляй ребёнка без взрослого во время опыта.'
      ],
      paperOnlyNotes: [
        'Если нельзя провести опыт сегодня — обсудите его на бумаге и запишите ожидаемый результат.',
        'Наблюдения важнее «идеального» результата.'
      ]
    },
    whatThisLessonTeaches: 'Собрать неделю в один проект: карта, масштаб, дробь, деление и физический опыт с наблюдением.',
    keyMessage: 'Хорошая наука — это наблюдение + запись + вывод.',
    howToExplain: 'Скажите: «Мы проверяем, а не гадаем. Записывай, что видишь, и объясняй, что думаешь.»',
    exampleDialogue:
      'Parent: Клеток всего?\nChild: 12.\nParent: Метров?\nChild: 72.\nParent: Периметр листа?\nChild: 54 см.\nParent: 72 ÷ 8 = ?\nChild: 9 минут.\nParent: Среднее по 3 попыткам?\nChild: 10 секунд.',
    readingSupport: 'Слова «поверхностное», «натяжение» — проговорите вместе.',
    russianSpeechGoal: 'Анита говорит: «Я нарисовала карту. Я сделала опыт. Вода держалась благодаря поверхностному натяжению.»',
    mathAnswerKey:
      'math-map-route: 5 + 4 + 3 = 12 клеток; 12 × 6 = 72 метра.\n' +
      'math-map-perimeter: 16 + 11 + 16 + 11 = 54; 2 × (16 + 11) = 54 см.\n' +
      'math-map-points: 1/2 от 40 = 20; 1/4 от 40 = 10; 40 − 20 − 10 = 10 мест.\n' +
      'math-travel-time: 72 ÷ 8 = 9 минут.\n' +
      'math-experiment-trials: 8 + 12 + 10 = 30 секунд; 30 ÷ 3 = 10 секунд в среднем.',
    experimentAnswerKey:
      'Наблюдение: вода не выливается сразу; ткань удерживает воду.\n' +
      'Объяснение: поверхностное натяжение создаёт тонкую водную плёнку в маленьких отверстиях ткани; плёнка держит воду.\n' +
      'Что изменится: большие дырочки — плёнка сразу рвётся, вода течёт быстро; тряска банки тоже разрушает плёнку.\n' +
      'Безопасность: только с родителем; только над раковиной или тазом; не тяжёлая банка; не трясти банку.',
    spellingAnswerKey: 'вода\nткань\nбанка\nдержится\nопыт\nвывод',
    answerKey: [
      { taskId: 'reading-review-words', answer: 'Попытка русского чтения 10 слов недели.' },
      { taskId: 'oral-defense-reading', answer: 'Попытка русского чтения 5 фраз защиты.' },
      { taskId: 'math-map-route', answer: '12 клеток; 72 метра.' },
      { taskId: 'math-map-perimeter', answer: '54 см (обоими способами).' },
      { taskId: 'math-map-points', answer: '20; 10; 10.' },
      { taskId: 'math-travel-time', answer: '9 минут.' },
      { taskId: 'math-experiment-trials', answer: '30 секунд всего; 10 в среднем.' },
      { taskId: 'physics-water-cloth', answer: 'Наблюдение и вывод про поверхностное натяжение.' },
      { taskId: 'logic-experiment-why', answer: 'Плёнка воды; большие дырочки/тряска разрушают.' },
      { taskId: 'spelling-day5', answer: 'вода, ткань, банка, держится, опыт, вывод.' },
      { taskId: 'speech-day5', answer: 'Защита проекта по-русски.' }
    ],
    check: [
      'Анита собрала карту 10×10 с четырьмя точками.',
      'Анита посчитала периметр 16×11 двумя способами.',
      'Анита нашла 1/2 и 1/4 от 40 и остаток.',
      'Анита посчитала время 72 ÷ 8 = 9.',
      'Анита провела опыт с родителем и заполнила поля наблюдений.',
      'Анита защитила проект по-русски.'
    ],
    spellingCheck: 'Проверить вода, ткань, банка, держится, опыт, вывод.',
    readingCheck: 'Главное — попытка чтения и защита проекта.',
    practicalCheck: 'Опыт делать над раковиной или тазом, только с родителем.',
    drawingCriteria: 'Карта 10×10 с точками S, T, B, H и подписью «1 клетка = 6 м».',
    commonMistakes: [
      '«Периметр = длина + ширина.» Правильно: все четыре стороны.',
      '«72 ÷ 8 = 8.» Правильно: 9.',
      '«Опыт можно делать одной.» Правильно: только с родителем.'
    ],
    discussionPrompts: [
      'Что удивительного в опыте?',
      'Почему вода держится в маленьких дырочках?',
      'Что было бы, если ткань — с большими дырами?'
    ],
    ifEasy: 'Добавить 4-ю попытку опыта и найти новое среднее по 4 попыткам.',
    ifDifficult: 'Оставить только карту 10×10, периметр и одну попытку опыта.',
    rewardGuidance: 'Базовая награда — 5 нимов. Бонус за аккуратную карту, полные поля опыта и ясную защиту.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Убедитесь, что банка лёгкая, ткань плотно натянута и опыт над раковиной или тазом.',
    safetyNotes: 'Только с родителем. Только над раковиной или тазом. Не использовать тяжёлую банку. Не трясти банку. Никаких настоящих адресов на карте — карта учебная и выдуманная.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 5,
    max: 8,
    label: 'Награда за проект',
    display: '5 нимов',
    bonuses: [
      { id: 'neat-project-map', label: 'Бонус за аккуратную карту 10×10', amount: 1 },
      { id: 'complete-experiment-fields', label: 'Бонус за полные поля наблюдений', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-review-words',
      'oral-defense-reading',
      'math-map-route',
      'math-map-perimeter',
      'math-map-points',
      'math-travel-time',
      'math-experiment-trials',
      'physics-water-cloth',
      'logic-experiment-why',
      'spelling-day5',
      'speech-day5'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-5-day-1'
};

export default day5;
